import axios from 'axios';
import qs from 'qs';
import {
  buildWebStorage,
  defaultKeyGenerator,
  setupCache,
  CacheRequestConfig,
} from 'axios-cache-interceptor/dist/index.bundle';
import { makeTableProperty, persistLogin, responseJsonIgnore } from "@src/utils/utils";
import { errorDialog } from "@src/components";
import { decrementRequestCount, incrementRequestCount, setRequestCount } from "@src/store/slice/appSettingsSlice";
import { languageOptions, langOptions } from "@src/assets/language";
import { getCurrentLanguage } from '@src/utils/language';
import dayjs from '@src/utils/day';

export const CancelToken = axios.CancelToken;
const source = CancelToken.source();
const controller = new AbortController();
const inFlightCaptchaKeys = new Set();

const getCaptchaPayload = (data) => {
  if (!data) {
    return null;
  }

  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (error) {
      return null;
    }
  }

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return {
      captchaKey: data.get('captchaKey'),
    };
  }

  if (typeof data === 'object') {
    return data;
  }

  return null;
};

const getCaptchaKey = (config) => getCaptchaPayload(config?.data)?.captchaKey;

const createApiError = ({
  message,
  code,
  status,
  data,
  config,
  response,
  cause,
}) => {
  const apiError = new Error(message || 'Request failed');

  apiError.name = 'ApiError';
  apiError.code = code;
  apiError.status = status;
  apiError.data = data;
  apiError.config = config;
  apiError.response = response;

  if (cause) {
    apiError.cause = cause;
  }

  return apiError;
};

const normalizeApiError = (error, fallbackMessage) => {
  if (error instanceof Error) {
    return error;
  }

  const response = error?.response || error;

  return createApiError({
    message:
      response?.data?.error?.message ||
      response?.data?.message ||
      fallbackMessage ||
      'Request failed',
    code: response?.data?.error?.code || response?.data?.code,
    status: response?.status,
    data: response?.data,
    config: response?.config || error?.config,
    response,
  });
};

const releaseCaptchaKey = (config) => {
  const captchaKey = config?.__captchaKey;

  if (captchaKey) {
    inFlightCaptchaKeys.delete(captchaKey);
  }
};

export { getCurrentLanguage };

const isEnglishLanguage = () => getCurrentLanguage() === 'en';

// Kept as a single choke point so a bad merge can't silently drop the
// English text and fall back to Turkish-only dialogs again.
const pickByLanguage = (en, tr) => (isEnglishLanguage() ? en : tr);

window.ReduxDispatch = () => null;
window.ReactHistory = {
  push: () => null
}

const Api = axios.create({
  baseURL: process.env.VITE_PUBLIC_HOST_API,
  timeout: 180000,
  headers: {
    'Access-Control-Allow-Methods': 'DELETE, POST, GET, OPTIONS',
    'Access-Control-Allow-Headers':
      'Authorization,Content-Type, Accept, X-Requested-With, remember-me',
    'Content-Type': 'application/json',
    'Access-Control-Allow-Credentials': 'true',
    'Access-Control-Max-Age': '3600',
    'x-last-ui-deploy-time': dayjs(process.DEPLOY_TIME).utc(true).utcOffset(180).toString(),
    'Content-Language': getCurrentLanguage(),
    'lang': getCurrentLanguage() === languageOptions.english ? langOptions.english : langOptions.turkey,
  },
  signal: controller.signal,
  cancelToken: source.token,
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'brackets' });
  },
});

Api?.interceptors?.request?.use(async (config) => {
  const currentLanguage = getCurrentLanguage();
  config.headers.set('Content-Language', currentLanguage);
  config.headers.set(
    'lang',
    currentLanguage === languageOptions.english ? langOptions.english : langOptions.turkey,
  );

  const captchaKey = getCaptchaKey(config);
  if (captchaKey) {
    if (inFlightCaptchaKeys.has(captchaKey)) {
      throw createApiError({
        message: 'Duplicate CAPTCHA request blocked on client.',
        code: 'DuplicateCaptchaRequest',
        status: 409,
        data: {
          code: 'DuplicateCaptchaRequest',
          message: 'Duplicate CAPTCHA request blocked on client.',
        },
        config,
        response: {
          config,
          data: {
            code: 'DuplicateCaptchaRequest',
            message: 'Duplicate CAPTCHA request blocked on client.',
          },
          status: 409,
        },
      });
    }

    inFlightCaptchaKeys.add(captchaKey);
    config.__captchaKey = captchaKey;
  }

  config.metadata = { startTime: Date.now() };
  if (config.url !== 'Notifications/getPendingNotifications' && !config.headers['x-skip-loader']) {
    await window.ReduxDispatch(incrementRequestCount());
  }
  if (!config.headers.has('x-dont-document-blur')) {
    document?.activeElement?.blur();
  } else {
    config.headers.delete('x-dont-document-blur')
  }
  return config;
},
  function (error) {
    throw normalizeApiError(error, 'Request configuration failed');
  }
);

let redirectToLoginResponse401 = false;
Api?.interceptors?.response?.use(
  async function (response) {
    releaseCaptchaKey(response.config);
    if (response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
    }
    if (response.config.url !== 'Notifications/getPendingNotifications' && !response.config.headers['x-skip-loader']) {
      await window.ReduxDispatch(decrementRequestCount());
    }
    const data = responseJsonIgnore(makeTableProperty(response.data));
    return {
      ...response,
      data,
    };
  },
  async function (error) {
    releaseCaptchaKey(error.config || error.response?.config);
    if (error.config?.metadata) {
      const duration = Date.now() - error.config.metadata.startTime;
    }
    if (error?.code === 'ECONNABORTED' && error?.message?.includes('timeout')) {
      errorDialog({
        title: pickByLanguage('Connection Error', 'Bağlantı Hatası'),
        message: pickByLanguage(
          'No response was received from the server. Please try again later.',
          'Sunucudan yanıt alınamadı. Lütfen daha sonra tekrar deneyin.',
        ),
      });
    }
    await window.ReduxDispatch(setRequestCount(0));
    handleErrorResponse(error);
    return Promise.reject(error.response);
  }
);

const handleErrorResponse = (error) => {
  switch (error.response?.status) {
    case 401:
      if (error.request?.responseURL?.includes('Auth/logout'))
        return;

      if (!redirectToLoginResponse401) {
        redirectToLoginResponse401 = true;
        return handleLoginRedirect(error);
      }
      break;
    case 403:
      window.ReactHistory.push('/dashboard')
      break;
    case 406:
      controller.abort();
      handle406Error(error);
      return new Promise(() => null);
    case 500:
    case 400:
      handle400And500Errors(error);
      break;
    case 410:
      window.ReactHistory.push('/login');
      break;
  }
};

const handleLoginRedirect = async (error) => {
  const NoRegisteredProduct = error.response?.data?.error?.code === 'NoRegisteredProduct';

  if (NoRegisteredProduct) globalThis.ReactHistory.push('/dashboard');

  const sessionTerminatedMessage = pickByLanguage(
    'Your session has been terminated.',
    'Aynı kullanıcı adı ile yeni bir giriş tespit edildiği için bu oturum sonlandırılmıştır.\n İşleminize devam etmek istiyorsanız lütfen yeniden giriş yapınız.',
  );
  const message = error.response?.data?.error?.message
    ? `<div style="text-align:center; line-height:30px;">${error.response?.data?.error?.message}</div>`
    : sessionTerminatedMessage;

  const okText = NoRegisteredProduct
    ? pickByLanguage('OK', 'Tamam')
    : pickByLanguage('Login', 'Giriş Yap');

  return errorDialog({
    title: pickByLanguage('Attention', 'Dikkat'),
    message,
    titleLeftIcon: 'warning',
    okText,
    okCancel: false,
    closeIcon: false,
    onOk: async () => {
      if (NoRegisteredProduct) {
        globalThis.ReactHistory.push('/dashboard');
      } else if (globalThis.ReduxDispatch) {
        await persistLogin(globalThis.ReduxDispatch, true);
      } else {
        globalThis.ReactHistory.push('/login');
      }
    },
  });

};

const handle406Error = (error) => {
  controller.abort();
  errorDialog({
    title: pickByLanguage('Error', 'Hata'),
    message: error.response.data.message,
    onOk: async () => {
      await persistLogin(window.ReduxDispatch, false);
    },
  });
};

const handle400And500Errors = (error) => {
  if (
    error.response?.data?.error?.message === '1' ||
    error.response?.data?.error?.code === 'ValidationErrors' ||
    error.response?.data?.error?.message === 'NotFound ' ||
    error.response?.data?.code === 'InvalidCaptchaKey' ||
    error.request?.responseURL?.includes('CardOrderDetails/add') ||
    error.request?.responseURL?.includes('CardOrderDetails/update') ||
    error.request?.responseURL?.includes('CardOrderDetails/cardorderdetailexcelupload')
  ) {
    return;
  }
  let modal = errorDialog({
    title: pickByLanguage('Error', 'Hata'),
    message:
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      pickByLanguage('An error occurred', 'Hata ile karşılaşıldı'),
    onOk: async () => {
      modal?.destroy();
    },
  });
};

// The backend localizes its responses through the Content-Language header, so the very same URL
// returns different text per language. Cache entries must therefore never be shared between
// languages - otherwise switching the language replays the previous language's cached payload
// (menus, product names, lookup labels...) until the - sometimes day long - TTL expires.
// This covers calls that pass their own `id` as well, since `defaultKeyGenerator` returns that id.
const languageAwareKeyGenerator = (request, meta) =>
  `${defaultKeyGenerator(request, meta)}::${getCurrentLanguage() || ''}`;

export const axiosInstance = setupCache(Api, {
  ttl: 1000,
  storage: buildWebStorage(window.localStorage, 'axios-cache:'),
  // Ignore the server's cache-control headers; a flat 1s TTL applies to every GET.
  // This collapses duplicate concurrent GET calls into a single SP round-trip.
  interpretHeader: false,
  generateKey: languageAwareKeyGenerator,
  cachePredicate: {
    statusCheck: (status) => [200].includes(status),
  },
});

// In-flight request deduplication:
// if the same GET request (URL + params + body) comes in from 2 places at
// once, only one real HTTP/SP call is made and the second caller gets the
// same promise back. Only active for GET, and only when cache isn't already
// opt-in; downloads (responseType !== json) and calls with an explicit
// cancelToken/signal are excluded to avoid side-effect risk.
const inflightRequests = new Map();

const buildInflightKey = (options) => {
  const method = (options?.method || 'get').toLowerCase();
  if (method !== 'get') return null;
  if (options?.cache) return null; // cache zaten dedupe ediyor
  if (options?.responseType && options.responseType !== 'json') return null;
  if (options?.cancelToken || options?.signal) return null;
  try {
    const paramsKey = options?.params ? qs.stringify(options.params, { arrayFormat: 'brackets' }) : '';
    const dataKey = options?.data ? JSON.stringify(options.data) : '';
    return `${method}:${options.url}:${paramsKey}:${dataKey}:${getCurrentLanguage() || ''}`;
  } catch (_) {
    return null;
  }
};

/**
 *
 * @param options {CacheRequestConfig}
 */
export const api = (options) => {
  if (options?.cache) {
    if (!options.cache.hasOwnProperty('interpretHeader')) {
      options.cache.interpretHeader = false;
    }
    if (!options.cache.hasOwnProperty('cachePredicate')) {
      options.cache.cachePredicate = {
        statusCheck: (status) => [200].includes(status)
      }
    }
  }
  const inflightKey = buildInflightKey(options);
  if (inflightKey && inflightRequests.has(inflightKey)) {
    return inflightRequests.get(inflightKey);
  }
  const promise = axiosInstance(options);
  if (inflightKey) {
    inflightRequests.set(inflightKey, promise);
    const cleanup = () => inflightRequests.delete(inflightKey);
    promise.then(cleanup, cleanup);
  }
  return promise;
}

/**
 * Drops a cached response by the `id` a service passes to `api()`. Cache keys carry a language
 * suffix (see `languageAwareKeyGenerator`), so every language variant of that id is removed.
 */
export const invalidateApiCache = (id) => {
  const prefix = `axios-cache:${id}`;
  window.localStorage.removeItem(prefix);
  Object.values(languageOptions).forEach((language) =>
    window.localStorage.removeItem(`${prefix}::${language}`),
  );
};

export const setAuthorization = (AuthorizationText) => {
  axiosInstance.defaults.headers.common.Authorization = AuthorizationText;
};

if (process.env.VITE_MODE !== 'master') {
  window.SetBaseUrl = (baseUrl) => axiosInstance.defaults.baseURL = baseUrl;
}
export const apiInterceptors = (dispatch, history) => {
  window.ReduxDispatch = dispatch;
  window.ReactHistory = history;
}
